import { generateEmailFormatErrorMessage, generateErrorMessage } from '../../../utils/error-message';
import { isNewErrorMessagingFlagActive } from '../../../utils/error-message-flag';

export type IFieldName =
    | 'Ssn'
    | 'FirstName'
    | 'LastName'
    | 'EmailAddress'
    | 'MailingAddressLine1'
    | 'MailingCity'
    | 'MailingState'
    | 'MailingZipCode'
    | 'IsMailingHomeAddressSame'
    | 'HomeAddressLine1'
    | 'HomeCity'
    | 'HomeState'
    | 'HomeZipCode'
    | 'Mailing'
    | 'PrimaryPhoneNumber'
    | 'MonthlyIncome'
    | 'DateOfBirth'
    | 'SelectedCardId'
    | 'MobilePhoneNumber'
    | 'MobilePhoneNumberAgree'
    | 'PrivacyPolicyAgree'
    | 'ConsumerAcknowledgement'
    | 'AuthorizedUserFirstName'
    | 'AuthorizedUserLastName'
    | 'IsAuthorizedUserSpouse'
    | 'IsAuthorizedUserSelected'
    | 'IsRushProcessSelected'
    | 'IsCreditProtectionSelected'
    | 'CreditProtectionInitials'
    | 'StateZipCombination'
    | 'HomeStateZipCombination'
    | 'HomeAddress'
    | 'AuthorizedUserDateOfBirth'
    | 'MailingAddress'
    | 'MiddleInitial'
    | 'MailingAddressLine2'
    | 'HomeAddressLine2'
    | 'SecondaryPhoneNumber'
    | 'AuthorizedUserMiddleInitial'
    | 'Under19YearDOB'
    | 'NameSuffix'
    | 'AddrAndAptNumMatch'
    | 'HomeAddrAndAptNumMatch'
    | 'TermsAndConditions';

export type TErrorType = 'RequiredField' | 'NotValid' | 'NotEligible' | 'ThirdParty';

export interface IPushdownErrorObject {
    FieldName: IFieldName;
    ErrorMessage: string;
    ErrorType: TErrorType;
    htmlError?: string;
}

const getPushdownError = (
    fieldName: IFieldName,
    errorType: TErrorType,
    homeAddressPrimary?: boolean,
) => {

    const validationMessages: IPushdownErrorObject[] = [
        {
            FieldName: 'Ssn',
            ErrorMessage: generateErrorMessage('Social Security Number', 'missingValue'),
            ErrorType: 'RequiredField',
            htmlError: generateErrorMessage('<a href="#socialSecurity-field-container" id="pushdown-socialSecurity">Social Security Number</a>', 'missingValue'),
        },
        {
            FieldName: 'FirstName',
            ErrorMessage: generateErrorMessage('First Name', 'missingValue'),
            ErrorType: 'RequiredField',
            htmlError: generateErrorMessage('<a href="#firstName-field-container" id="pushdown-firstName">First Name</a>', 'missingValue'),
        },
        {
            FieldName: 'LastName',
            ErrorMessage: generateErrorMessage('Last Name', 'missingValue'),
            ErrorType: 'RequiredField',
            htmlError: generateErrorMessage('<a href="#lastName-field-container" id="pushdown-lastName">Last Name</a>', 'missingValue'),
        },
        {
            FieldName: 'EmailAddress',
            ErrorMessage: `${generateErrorMessage('Email Address', 'missingValue')}: email@domain.com`,
            ErrorType: 'RequiredField',
            htmlError: isNewErrorMessagingFlagActive ? `${generateErrorMessage('<a href="#emailAddress-field-container" id="pushdown-emailAddress">Email Address</a>', 'missingValue')}: email@domain.com` : 'Please provide your <a href="#emailAddress-field-container" id="pushdown-emailAddress">Email Address (email@domain.com)</a>',
        },
        {
            FieldName: 'MailingAddressLine1',
            ErrorMessage: generateErrorMessage('Mailing Street Address', 'missingValue'),
            ErrorType: 'RequiredField',
            htmlError: generateErrorMessage(`<a href="#mailingAddress-field-container" id="pushdown-mailingAddress">${homeAddressPrimary ? 'Home' : 'Mailing'} Street Address</a>`, 'missingValue'),
        },
        {
            FieldName: 'MailingCity',
            ErrorMessage: generateErrorMessage('Mailing Address City', 'missingValue'),
            ErrorType: 'RequiredField',
            htmlError: generateErrorMessage(`<a href="#mailingCity-field-container" id="pushdown-mailingCity">${homeAddressPrimary ? 'Home Address ' : !isNewErrorMessagingFlagActive ? 'Mailing Address ' : ''}City</a>`, 'missingValue'),
        },
        {
            FieldName: 'MailingState',
            ErrorMessage: generateErrorMessage('Mailing Address State', 'missingValue'),
            ErrorType: 'RequiredField',
            htmlError: generateErrorMessage(`<a href="#mailingState-field-container" id="pushdown-mailingState">${homeAddressPrimary ? 'Home Address ' : !isNewErrorMessagingFlagActive ? 'Mailing Address ' : ''}State</a>`, 'missingValue'),
        },
        {
            FieldName: 'MailingZipCode',
            ErrorMessage: generateErrorMessage('Mailing Address ZIP Code', 'missingValue'),
            ErrorType: 'RequiredField',
            htmlError: generateErrorMessage(`<a href="#mailingZipCode-field-container" id="pushdown-mailingZipCode">${homeAddressPrimary ? 'Home Address ' : !isNewErrorMessagingFlagActive ? 'Mailing Address ' : ''}ZIP Code</a>`, 'missingValue'),
        },
        {
            FieldName: 'IsMailingHomeAddressSame',
            ErrorMessage:
                'Confirmation that Mailing Address is Home Street Address',
            ErrorType: 'RequiredField',
        },
        {
            FieldName: 'HomeAddressLine1',
            ErrorMessage: generateErrorMessage('Home Street Address', 'missingValue'),
            ErrorType: 'RequiredField',
            htmlError: generateErrorMessage(`<a href="#homeAddress-field-container" id="pushdown-homeAddress">${homeAddressPrimary ? 'Mailing' : 'Home'} Street Address</a>`, 'missingValue'),
        },
        {
            FieldName: 'HomeAddressLine1',
            ErrorMessage: 'Home Address cannot be a PO Box',
            ErrorType: 'NotValid',
        },
        {
            FieldName: 'HomeCity',
            ErrorMessage: generateErrorMessage('Home Address City', 'missingValue'),
            ErrorType: 'RequiredField',
            htmlError: generateErrorMessage(`<a href="#homeCity-field-container" id="pushdown-homeCity">${homeAddressPrimary ? 'Mailing' : 'Home'} Address City</a>`, 'missingValue'),
        },
        {
            FieldName: 'HomeState',
            ErrorMessage: generateErrorMessage('Home Address State', 'missingValue'),
            ErrorType: 'RequiredField',
            htmlError: generateErrorMessage(`<a href="#homeState-field-container" id="pushdown-homeState">${homeAddressPrimary ? 'Mailing' : 'Home'} Address State</a>`, 'missingValue'),
        },
        {
            FieldName: 'HomeZipCode',
            ErrorMessage: generateErrorMessage('Home Address ZIP Code', 'missingValue'),
            ErrorType: 'RequiredField',
            htmlError: generateErrorMessage(`<a href="#homeZipCode-field-container" id="pushdown-homeZipCode">${homeAddressPrimary ? 'Mailing' : 'Home'} Address ZIP Code</a>`, 'missingValue'),
        },
        {
            FieldName: 'PrimaryPhoneNumber',
            ErrorMessage: generateErrorMessage('Phone Number', 'missingValue'),
            ErrorType: 'RequiredField',
            htmlError: generateErrorMessage(`<a href="#phone-field-container" id="pushdown-phone">${isNewErrorMessagingFlagActive ? '' : 'Main '}Phone Number</a>`, 'missingValue'),
        },
        {
            FieldName: 'MonthlyIncome',
            ErrorMessage:
                generateErrorMessage('Total Monthly Income', 'missingValue'),
            ErrorType: 'RequiredField',
            htmlError: generateErrorMessage(`<a href="#monthlyIncome-field-container" id="pushdown-monthlyIncome">Total Monthly Income${isNewErrorMessagingFlagActive ? '' : ' from all sources'}</a>`, 'missingValue'),
        },
        {
            FieldName: 'DateOfBirth',
            ErrorMessage: generateErrorMessage('Date of Birth', 'missingValue'),
            ErrorType: 'RequiredField',
            htmlError: isNewErrorMessagingFlagActive ? generateErrorMessage('<a href="#dob-field-container" id="pushdown-dob">Date of Birth</a>', 'missingValue') : 'Please provide your <a href="#dob-field-container" id="pushdown-dob">Date of Birth (MM-DD-YYYY)</a>',
        },
        {
            FieldName: 'SelectedCardId',
            ErrorMessage: 'Card Design selection is required',
            ErrorType: 'RequiredField',
            htmlError: isNewErrorMessagingFlagActive ? '<a href="#plastic-card-design" id="pushdown-plastic-card-design">Card Design</a> selection is required' : 'Please select a <a href="#plastic-card-design" id="pushdown-plastic-card-design">Card Design</a>',
        },
        {
            FieldName: 'MobilePhoneNumber',
            ErrorMessage: 'Please enter a Mobile Phone Number',
            ErrorType: 'RequiredField',
        },
        {
            FieldName: 'MobilePhoneNumberAgree',
            ErrorMessage:
                'Please agree the Mobile Phone Number statement to proceed',
            ErrorType: 'RequiredField',
        },
        {
            FieldName: 'PrivacyPolicyAgree',
            ErrorMessage: 'Please agree the Privay Policy statement to proceed',
            ErrorType: 'RequiredField',
        },
        {
            FieldName: 'ConsumerAcknowledgement',
            ErrorMessage: 'Please agree the to the Customer Report Written Authorization',
            ErrorType: 'RequiredField',
            htmlError: 'Please agree to the <a href="#consumer-report">Consumer Report Written Authorization</a>',
        },
        {
            FieldName: 'AuthorizedUserFirstName',
            ErrorMessage: generateErrorMessage('Authorized User&#x27;s First Name', 'missingValue'),
            ErrorType: 'RequiredField',
            htmlError: generateErrorMessage('<a href="#auFirstName-field-container" id="pushdown-auFirstName">Authorized User&#x27;s First Name</a>', 'missingValue'),
        },
        {
            FieldName: 'AuthorizedUserLastName',
            ErrorMessage: generateErrorMessage('Authorized User&#x27;s Last Name', 'missingValue'),
            ErrorType: 'RequiredField',
            htmlError: generateErrorMessage('<a href="#auLastName-field-container" id="pushdown-auLastName">Authorized User&#x27;s Last Name</a>', 'missingValue'),
        },
        {
            FieldName: 'IsAuthorizedUserSpouse',
            ErrorMessage: 'Select if Authorized User is your spouse',
            ErrorType: 'RequiredField',
            htmlError: isNewErrorMessagingFlagActive ? 'Select if <a href="#auSpouse-field-container" id="pushdown-auSpouse">Authorized User is your spouse</a>' : 'Please select <a href="#auSpouse-field-container" id="pushdown-auSpouse">if Authorized User is your spouse</a>',
        },
        {
            FieldName: 'IsAuthorizedUserSelected',
            ErrorMessage: 'Please select an option for Authorized User',
            ErrorType: 'RequiredField',
        },
        {
            FieldName: 'IsRushProcessSelected',
            ErrorMessage: 'Please select an option for Rush Processing Offer',
            ErrorType: 'RequiredField',
        },
        {
            FieldName: 'IsCreditProtectionSelected',
            ErrorMessage:
                'Please check the "Yes" box to complete your purchase of Credit Protection',
            ErrorType: 'RequiredField',
        },
        {
            FieldName: 'CreditProtectionInitials',
            ErrorMessage:
                'Provide your initials to confirm enrollment',
            ErrorType: 'RequiredField',
            htmlError: isNewErrorMessagingFlagActive ? 'Provide your <a href="#creditProtection-field-container" id="pushdown-creditProtection">initials to confirm enrollment</a>' : 'Please provide your initials to <a href="#creditProtection-field-container" id="pushdown-creditProtection">enroll in the Optional Credit Protection Program</a>',
        },
        {
            FieldName: 'AuthorizedUserDateOfBirth',
            ErrorMessage:
                generateErrorMessage('Date of Birth', 'missingValue'),
            ErrorType: 'RequiredField',
            htmlError:
                isNewErrorMessagingFlagActive ? generateErrorMessage('<a href="#auDateOfBirth-field-container" id="pushdown-auDateOfBirth">Authorized User&#x27;s Date of Birth</a>', 'missingValue') : 'Please provide the <a href="#auDateOfBirth-field-container" id="pushdown-auDateOfBirth">Authorized User&#x27;s Date of Birth (MM-DD-YYYY)</a>',
        },
        {
            FieldName: 'FirstName',
            ErrorMessage: 'Please enter valid First Name',
            ErrorType: 'NotValid',
            htmlError: generateErrorMessage('<a href="#firstName-field-container" id="pushdown-firstName">First Name</a>'),
        },
        {
            FieldName: 'LastName',
            ErrorMessage: 'Please enter valid Last Name',
            ErrorType: 'NotValid',
            htmlError: generateErrorMessage('<a href="#lastName-field-container" id="pushdown-lastName">Last Name</a>'),
        },
        {
            FieldName: 'EmailAddress',
            ErrorMessage: generateEmailFormatErrorMessage('Email Address'),
            ErrorType: 'NotValid',
            htmlError: isNewErrorMessagingFlagActive ? generateEmailFormatErrorMessage('<a href="#emailAddress-field-container" id="pushdown-emailAddress">Email Address</a>') : 'Please provide your <a href="#emailAddress-field-container" id="pushdown-emailAddress">Email Address (email@domain.com)</a>',
        },
        {
            FieldName: 'EmailAddress',
            ErrorMessage: generateErrorMessage('Email Address', 'thirdParty', 'Email'),
            ErrorType: 'ThirdParty',
            htmlError: isNewErrorMessagingFlagActive ? generateErrorMessage('<a href="#emailAddress-field-container" id="pushdown-emailAddress">Email Address</a>', 'thirdParty', 'Email') : 'Please provide your <a href="#emailAddress-field-container" id="pushdown-emailAddress">Email Address (email@domain.com)</a>',
        },
        {
            FieldName: 'StateZipCombination',
            ErrorMessage: 'Please enter a valid State and ZIP Code combination',
            ErrorType: 'NotValid',
        },
        {
            FieldName: 'HomeStateZipCombination',
            ErrorMessage: 'Please enter a valid State and ZIP Code combination',
            ErrorType: 'NotValid',
        },
        {
            FieldName: 'HomeAddress',
            ErrorMessage: 'Home Street Address Cannot be a PO Box',
            ErrorType: 'NotValid',
        },
        {
            FieldName: 'HomeCity',
            ErrorMessage: 'Please enter valid Home Address City',
            ErrorType: 'NotValid',
        },
        {
            FieldName: 'MonthlyIncome',
            ErrorMessage:
                generateErrorMessage('Total Monthly Income'),
            ErrorType: 'NotValid',
            htmlError: generateErrorMessage(`<a href="#monthlyIncome-field-container" id="pushdown-monthlyIncome">Total Monthly Income${isNewErrorMessagingFlagActive ? '' : ' from all sources'}</a>`),
        }, 
        {
            FieldName: 'DateOfBirth',
            ErrorMessage: generateErrorMessage('Date of Birth'),
            ErrorType: 'NotValid',
            htmlError: isNewErrorMessagingFlagActive ? generateErrorMessage('<a href="#dob-field-container" id="pushdown-dob">Date of Birth</a>') : 'Please provide your <a href="#dob-field-container" id="pushdown-dob">Date of Birth (MM-DD-YYYY)</a>',
        },
        {
            FieldName: 'PrimaryPhoneNumber',
            ErrorMessage: 'Please enter a valid Phone Number',
            ErrorType: 'NotValid',
            htmlError: generateErrorMessage(`<a href="#phone-field-container" id="pushdown-phone">${isNewErrorMessagingFlagActive ? '' : 'Main '}Phone Number</a>`, 'missingValue'),
        },
        {
            FieldName: 'PrimaryPhoneNumber',
            ErrorMessage: 'Please enter a valid Phone Number',
            ErrorType: 'ThirdParty',
            htmlError: generateErrorMessage(`<a href="#phone-field-container" id="pushdown-phone">${isNewErrorMessagingFlagActive ? '' : 'Main '}Phone Number</a>`, 'thirdParty', 'Number'),
        },
        {
            FieldName: 'Ssn',
            ErrorMessage: generateErrorMessage('Social Security Number'),
            ErrorType: 'NotValid',
            htmlError: generateErrorMessage('<a href="#socialSecurity-field-container" id="pushdown-socialSecurity">Social Security Number</a>'),
        },
        {
            FieldName: 'AuthorizedUserFirstName',
            ErrorMessage: generateErrorMessage('Authorized User&#x27;s First Name'),
            ErrorType: 'NotValid',
            htmlError: generateErrorMessage('<a href="#auFirstName-field-container" id="pushdown-auFirstName">Authorized User&#x27;s First Name</a>'),
        },
        {
            FieldName: 'AuthorizedUserLastName',
            ErrorMessage: generateErrorMessage('Authorized User&#x27;s Last Name'),
            ErrorType: 'NotValid',
            htmlError: generateErrorMessage('<a href="#auLastName-field-container" id="pushdown-auLastName">Authorized User&#x27;s Last Name</a>'),
        },
        {
            FieldName: 'AuthorizedUserDateOfBirth',
            ErrorMessage:
                generateErrorMessage('Authorized User&#x27;s Date of Birth'),
            ErrorType: 'NotValid',
        },
        {
            FieldName: 'AuthorizedUserDateOfBirth',
            ErrorMessage: 'Authorized User must be at least 15 years of age',
            ErrorType: 'NotEligible',
        },
        {
            FieldName: 'MailingAddress',
            ErrorMessage:
                'Address provided is outside our service area. You are not eligible for this program',
            ErrorType: 'NotEligible',
        },
        {
            FieldName: 'DateOfBirth',
            ErrorMessage:
                'Must be at least 18 years of age to apply. You are not eligible for this program',
            ErrorType: 'NotEligible',
        },
        {
            FieldName: 'MailingAddressLine1',
            ErrorMessage: generateErrorMessage('Mailing Address', 'thirdParty', 'Address'),
            ErrorType: 'NotValid',
            htmlError: generateErrorMessage('<a href="#mailingAddress-field-container" id="pushdown-mailingAddress">Mailing Address</a>', 'thirdParty', 'Address'),
        },
        {
            FieldName: 'MailingCity',
            ErrorMessage: 'Please enter valid Mailing Address City',
            ErrorType: 'NotValid',
        },
        {
            FieldName: 'MiddleInitial',
            ErrorMessage: 'Please enter a valid Middle Initial (optional)',
            ErrorType: 'NotValid',
        },
        {
            FieldName: 'MailingAddressLine2',
            ErrorMessage: 'Please enter a valid Apt/Unit # (optional)',
            ErrorType: 'NotValid',
        },
        {
            FieldName: 'HomeAddressLine2',
            ErrorMessage:
                'Please enter a valid Mailing Home Apt/Unit # (optional)',
            ErrorType: 'NotValid',
        },
        {
            FieldName: 'SecondaryPhoneNumber',
            ErrorMessage:
                'Please enter a valid Secondary Phone Number (optional)',
            ErrorType: 'NotValid',
        },
        {
            FieldName: 'AuthorizedUserMiddleInitial',
            ErrorMessage:
                'Please enter a valid Authorized User Middle Initial (optional)',
            ErrorType: 'NotValid',
        },
        {
            FieldName: 'MobilePhoneNumber',
            ErrorMessage: 'Please enter a valid Mobile Phone Number',
            ErrorType: 'NotValid',
        },
        {
            FieldName: 'Under19YearDOB',
            ErrorMessage:
                'Must be at least 18 years of age to apply, or 19 years of age for Alabama residents. You are not eligible for this program',
            ErrorType: 'NotEligible',
        },
        {
            FieldName: 'MiddleInitial',
            ErrorMessage: 'Please enter a valid Middle Initial',
            ErrorType: 'RequiredField',
        },
        {
            FieldName: 'NameSuffix',
            ErrorMessage: 'Please provide a valid Suffix',
            ErrorType: 'RequiredField',
            htmlError: 'Please provide a valid <a href="#suffix-field-container">Suffix</a>',
        },
        {
            FieldName: 'AddrAndAptNumMatch',
            ErrorMessage: 'Mailing address cannot be the same as apt/unit#',
            ErrorType: 'NotValid',
            htmlError: '<a href="#mailingAddress-field-container">Mailing Address</a> cannot be the same as Apt/Unit#',
        },
        {
            FieldName: 'HomeAddrAndAptNumMatch',
            ErrorMessage: 'Home address cannot be the same as apt/unit#',
            ErrorType: 'NotValid',
            htmlError: '<a href="#homeAddress-field-container">Home Address</a> cannot be the same as Apt/Unit#',
        },
        {
            FieldName: 'TermsAndConditions',
            ErrorMessage: 'Please agree to the †Disclosures and Terms & Conditions',
            ErrorType: 'RequiredField',
            htmlError: 'Please agree to the <a href="#terms-and-conditions"><sup>†</sup>Disclosures and Terms & Conditions</a>',
        },
    ];

    const error = validationMessages.find(validation => {
        return (
            validation.FieldName.toLowerCase() === (fieldName as IFieldName).toLowerCase() &&
            validation.ErrorType.toLowerCase() === errorType.toLowerCase()
        );
    });

    if (error) return error;

    return {
        ErrorMessage: fieldName + ' : ' + errorType,
        htmlError: `Please check the <a href="#${fieldName.toLowerCase()}-field-container">${fieldName}</a> field`,
    };
};

export default getPushdownError;
